import '@mantine/code-highlight/styles.css';

import { forwardRef, useEffect, useImperativeHandle, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Card,
  Code,
  Collapse,
  Divider,
  Drawer,
  Group,
  Space,
  Stack,
  Text,
  TextInput,
  Switch,
  Badge,
  FileInput,
  Image,
  SimpleGrid,
  Loader,
  Modal,
  Paper,
  Box,
  Notification,
} from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import {
  IconHelp,
  IconLock,
  IconChevronDown,
  IconChevronUp,
  IconUpload,
  IconTrash,
  IconEye,
  IconMapPin,
  IconX,
  IconPlus,
  IconEdit,
  IconCheck,
} from '@tabler/icons-react';
import { CodeHighlight } from '@mantine/code-highlight';

import { requestBody, responseBody } from '@/components/Settings/items/example';
import { processImages, getImage, getCanvasImages, deleteCanvasImage, saveStoreInfo } from '@mygpt/app';
import { requireSharedIntegration } from '@mygpt/app/config/integrations';

interface Props {
  documentForm: UseFormReturnType<any>;
  onOverrideChangeForConfigForm: (prop: string) => (data?: any) => void;
  aiId: string;
}

// Canvas类型
type CanvasType = 'none' | 'general' | 'map';

// 店铺检测结果
interface StoreDetection {
  id: string;
  name: string;
  x: number;
  y: number;
}

// 图片文件信息
interface ImageFile {
  id: number;
  ai_id: string;
  path: string;
  width: number;
  height: number;
  original_filename: string;
  s3_filename: string;
  analyse_status: number;
  created_at: string;
  // 本地临时字段
  file?: File;
  url?: string;
  detections?: StoreDetection[];
  analyse_status_text?: string;
}

export const CanvasForm = forwardRef(({ documentForm, onOverrideChangeForConfigForm, aiId }: Props, ref) => {
  const { t } = useTranslation();
  const [opened, { close, open }] = useDisclosure(false);
  const [canvasType, setCanvasType] = useState<CanvasType>('none');
  const [generalExpanded, setGeneralExpanded] = useState(false);
  const [mapExpanded, setMapExpanded] = useState(false);

  // General Canvas状态
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookUrlError, setWebhookUrlError] = useState('');

  // Map Canvas状态
  const [images, setImages] = useState<ImageFile[]>([]);
  const [previewImage, setPreviewImage] = useState<ImageFile | null>(null);
  const [selectedDetectionId, setSelectedDetectionId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [loadingImages, setLoadingImages] = useState(false);
  const [fileInputValue, setFileInputValue] = useState<File[]>([]);

  // 编辑模式状态 - 修改为按店铺单独管理编辑状态
  const [editingDetections, setEditingDetections] = useState<StoreDetection[]>([]);
  const [originalDetections, setOriginalDetections] = useState<StoreDetection[]>([]);
  const [editingDetectionId, setEditingDetectionId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [localEditModes, setLocalEditModes] = useState<{ [key: string]: boolean }>({});

  // 图片名称编辑状态
  const [editingImageId, setEditingImageId] = useState<number | null>(null);
  const [editingImageName, setEditingImageName] = useState<string>('');

  // 保存图片名称的API调用函数（基于现有saveStoreInfo的模式）
  const saveImageName = async (imageId: number, imageName: string) => {
    requireSharedIntegration('Canvas');
    const { post } = await import('@mygpt/app').then((m) => m.Http.use());
    return post(`${import.meta.env.VITE_CANVAS_API_BASE_URL}/save-info`, {
      image_id: imageId,
      ai_id: aiId,
      image_name: imageName,
    });
  };

  // 处理图片名称编辑
  const handleEditImageName = (imageId: number, currentName: string) => {
    setEditingImageId(imageId);
    setEditingImageName(currentName);
  };

  // 保存图片名称
  const handleSaveImageName = async () => {
    if (editingImageId === null || !editingImageName.trim()) return;

    setIsSaving(true);
    try {
      await saveImageName(editingImageId, editingImageName.trim());

      // 更新本地状态
      setImages((prev) =>
        prev.map((img) => (img.id === editingImageId ? { ...img, original_filename: editingImageName.trim() } : img))
      );

      // 退出编辑模式
      setEditingImageId(null);
      setEditingImageName('');

      showNotification({
        title: t('canvas.save_success'),
        message: t('canvas.image_name_update_success'),
        color: 'green',
      });
    } catch (error) {
      showNotification({
        title: '保存失败',
        message: '保存图片名称时发生错误，请重试',
        color: 'red',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // 取消编辑图片名称
  const handleCancelEditImageName = () => {
    setEditingImageId(null);
    setEditingImageName('');
  };

  // 图片元素引用
  const imageRef = useRef<HTMLImageElement>(null);
  // 图片容器引用
  const imageContainerRef = useRef<HTMLDivElement>(null);
  // 店铺卡片引用
  const detectionCardRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // 滚动到指定店铺卡片并添加高亮动画
  const scrollToDetectionCard = (detectionId: string) => {
    const cardElement = detectionCardRefs.current[detectionId];
    if (cardElement) {
      // 滚动到卡片位置
      cardElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });

      // 添加高亮动画
      cardElement.style.transform = 'scale(1.02)';
      cardElement.style.transition = 'all 0.3s ease';
      cardElement.style.boxShadow = '0 4px 12px rgba(25, 118, 210, 0.3)';

      // 动画结束后恢复
      setTimeout(() => {
        cardElement.style.transform = 'scale(1)';
        cardElement.style.boxShadow = '';
      }, 300);
    }
  };

  // 加载已上传的Canvas图片
  const loadCanvasImages = async () => {
    if (!aiId) return;

    setLoadingImages(true);
    try {
      const resp = await getCanvasImages({ bot_id: aiId });
      var response = resp.data;
      if (response.status === 'success' && response.images) {
        // 将API返回的数据转换为组件需要的格式
        const canvasImages: ImageFile[] = response.images.map((item) => ({
          id: item.id,
          ai_id: response.ai_id,
          path: item.path,
          width: item.width || 800, // 使用API返回的宽度，如果没有则用默认值
          height: item.height || 600, // 使用API返回的高度，如果没有则用默认值
          original_filename: item.origin_filename,
          s3_filename: '',
          analyse_status: 1, // 假设已处理完成
          created_at: '',
          url: item.path,
          detections: [],
        }));

        setImages(canvasImages);

        // 不自动展开，保持默认收起状态
        // 用户需要手动点击箭头来展开内容
      }
    } catch (error) {
      console.error('Failed to load canvas images:', error);
      // 如果加载失败，不显示错误提示，只是保持当前状态
    } finally {
      setLoadingImages(false);
    }
  };

  // 组件初始化时加载Canvas图片
  useEffect(() => {
    if (aiId) {
      loadCanvasImages();
    }
  }, [aiId]);

  // 监听form值变化，同步webhook URL和canvas类型
  useEffect(() => {
    if (documentForm.values.preview_panel_api !== undefined) {
      setWebhookUrl(documentForm.values.preview_panel_api || '');
    }

    // 同步canvas_type从服务器数据，优先使用服务器数据
    if (documentForm.values.canvas_type !== undefined && documentForm.values.canvas_type !== null) {
      const serverCanvasType = documentForm.values.canvas_type;
      let targetCanvasType: CanvasType = 'none';

      if (serverCanvasType === '1' || serverCanvasType === 1) {
        targetCanvasType = 'general';
      } else if (serverCanvasType === '2' || serverCanvasType === 2) {
        targetCanvasType = 'map';
      }

      // 更新本地状态
      setCanvasType(targetCanvasType);

      // 初始化时不自动展开，保持默认收起状态
      // 用户需要手动点击箭头来展开内容
    }
  }, [documentForm.values.preview_panel_api, documentForm.values.canvas_type]);

  // 暴露方法给父组件
  useImperativeHandle(ref, () => ({
    getCanvasType: () => canvasType,
    getGeneralConfig: () => ({ webhookUrl }),
    getMapImages: () => images,
  }));

  // 处理General Canvas开关
  const handleGeneralSwitch = (checked: boolean) => {
    const newType = checked ? 'general' : 'none';
    setCanvasType(newType);

    // 设置canvas_type并触发保存API
    const canvasTypeValue = checked ? '1' : '';
    documentForm.setFieldValue('canvas_type', canvasTypeValue);
    onOverrideChangeForConfigForm('canvas_type')(canvasTypeValue);

    if (checked) {
      setGeneralExpanded(true);
      setMapExpanded(false);
    } else {
      setGeneralExpanded(false);
    }
  };

  // 处理Map Canvas开关
  const handleMapSwitch = (checked: boolean) => {
    const newType = checked ? 'map' : 'none';
    setCanvasType(newType);

    // 设置canvas_type并触发保存API
    const canvasTypeValue = checked ? '2' : '';
    documentForm.setFieldValue('canvas_type', canvasTypeValue);
    onOverrideChangeForConfigForm('canvas_type')(canvasTypeValue);

    if (checked) {
      setMapExpanded(true);
      setGeneralExpanded(false);
    } else {
      setMapExpanded(false);
    }
  };

  // 处理文件上传
  const handleFileUpload = async (files: File[]) => {
    if (!files.length) return;

    try {
      // 调用真实API
      const resp = await processImages({
        ai_id: aiId,
        files: files,
      });
      var response = resp.data;
      if (response.status === 'success') {
        // 将API返回的数据转换为组件需要的格式
        const newImages: ImageFile[] = response.results.map((result) => ({
          ...result,
          url: result.path, // 使用path作为图片URL
          detections: [], // 初始状态下没有检测结果
        }));
        // console.log(newImages)
        setImages((prev) => [...prev, ...newImages]);

        showNotification({
          title: t('canvas.upload_success'),
          message: t('canvas.upload_success_message', { count: response.processed_count }),
          color: 'green',
        });
      }
    } catch (error) {
      // console.error('Failed to upload images:', error);
      showNotification({
        title: t('canvas.upload_error'),
        message: t('canvas.upload_error_message'),
        color: 'red',
      });
    } finally {
      // 清空文件输入，确保下次可以选择相同文件
      setFileInputValue([]);
    }
  };

  // 删除图片
  const handleDeleteImage = async (id: number) => {
    try {
      // 调用删除API
      await deleteCanvasImage({
        image_id: id,
        ai_id: aiId,
      });

      // API调用成功后，从本地状态中移除图片
      setImages((prev) => prev.filter((img) => img.id !== id));

      showNotification({
        title: t('canvas.delete_success'),
        message: t('canvas.delete_image_success_message'),
        color: 'green',
      });
    } catch (error) {
      // console.error('Failed to delete image:', error);
      showNotification({
        title: t('canvas.delete_error'),
        message: t('canvas.delete_image_error_message'),
        color: 'red',
      });
    }
  };

  // 拖拽处理
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files).filter((file) => file.type.startsWith('image/'));

    if (files.length > 0) {
      handleFileUpload(files);
    }
  };

  // 处理图片预览点击
  const handleImagePreview = async (image: ImageFile) => {
    try {
      // 调用get-image API获取详细信息和检测结果
      const resp = await getImage({
        image_id: image.id,
        ai_id: aiId,
      });
      var response = resp.data;

      // 检查解析状态
      if (response.analyse_status === 3) {
        // 解析失败，提示删除重新上传
        showNotification({
          title: t('canvas.image_analysis_failed'),
          message: t('canvas.image_analysis_failed_message'),
          color: 'red',
        });
        return;
      } else if (response.analyse_status !== 2) {
        // 其他状态（如处理中），显示处理中提示
        showNotification({
          title: t('canvas.image_processing'),
          message: t('canvas.image_processing_message'),
          color: 'orange',
        });
        return;
      }

      // 更新图片信息和检测结果
      const updatedImage: ImageFile = {
        ...image,
        ...response,
        width: response.image_width,
        height: response.image_height,
        url: response.path,
        detections: response.analyse_data,
        analyse_status_text: response.analyse_status_text,
      };

      // 更新状态中的图片信息
      setImages((prev) => prev.map((img) => (img.id === image.id ? updatedImage : img)));

      // 设置预览图片并重置选中状态
      setPreviewImage(updatedImage);
      setSelectedDetectionId(null);

      // 设置编辑数据和原始数据
      const detections = updatedImage.detections || [];
      setEditingDetections([...detections]);
      setOriginalDetections([...detections]);
      setEditingDetectionId(null);
      setLocalEditModes({});
    } catch (error) {
      // console.error('Failed to get image details:', error);
      showNotification({
        title: t('canvas.load_error'),
        message: t('canvas.load_error_message'),
        color: 'red',
      });
    }
  };

  const getCanvasStatus = (type: 'general' | 'map') => {
    const isEnabled = canvasType === type;
    let isConfigured = false;

    if (type === 'general') {
      isConfigured = !!webhookUrl;
    } else if (type === 'map') {
      isConfigured = images.length > 0;
    }

    return { isEnabled, isConfigured };
  };

  // 编辑相关函数
  const handleEditDetection = (detectionId: string) => {
    // 直接进入完整编辑状态：删除按钮 + 可编辑表单 + 保存/取消按钮
    setEditingDetectionId(detectionId);
    setLocalEditModes((prev) => ({ ...prev, [detectionId]: true }));
    setSelectedDetectionId(detectionId); // 确保编辑时该店铺被选中
  };

  const handleSaveDetection = async (detectionId: string, name: string, x: number, y: number) => {
    // 更新当前编辑的数据
    setEditingDetections((prev) =>
      prev.map((detection) => (detection.id === detectionId ? { ...detection, name, x, y } : detection))
    );

    // 调用全局保存API
    if (!previewImage || !aiId) return;

    setIsSaving(true);
    try {
      // 保存接口是整包落库（store_data 为全量 JSON），保存前必须把所有临时 ID
      // （new_ 前缀）升级为永久 ID 再落库：
      // 1. 否则「取消编辑」会把已保存的店铺误当作未保存的新店铺整体移除；
      // 2. 临时 ID 一旦落库，刷新后从后端回读（get-image 的 analyse_data）仍带
      //    new_ 前缀，同样的误删会在新会话里复发。
      const idMap: Record<string, string> = {};
      const updatedDetections = editingDetections.map((detection, index) => {
        const updated = detection.id === detectionId ? { ...detection, name, x, y } : detection;
        if (updated.id.startsWith('new_')) {
          const permanentId = `store_${Date.now()}_${index}`;
          idMap[updated.id] = permanentId;
          return { ...updated, id: permanentId };
        }
        return updated;
      });
      const savedId = idMap[detectionId] || detectionId;

      const storeData = JSON.stringify(updatedDetections);
      await saveStoreInfo({
        image_id: previewImage.id,
        ai_id: aiId,
        store_data: storeData,
      });

      // 更新本地状态（编辑列表同步替换为永久 ID）
      setEditingDetections([...updatedDetections]);
      setOriginalDetections([...updatedDetections]);

      // 更新images中的数据
      setImages((prev) =>
        prev.map((img) => (img.id === previewImage.id ? { ...img, detections: [...updatedDetections] } : img))
      );

      // 更新previewImage
      setPreviewImage((prev) => (prev ? { ...prev, detections: [...updatedDetections] } : null));

      // 退出编辑模式
      setEditingDetectionId(null);
      // 选中项若被改名，同步替换为永久 ID
      if (selectedDetectionId) {
        setSelectedDetectionId(idMap[selectedDetectionId] || selectedDetectionId);
      }
      // localEditModes 的 key 同步迁移到永久 ID，并退出当前项的编辑态
      setLocalEditModes((prev) => {
        const newModes: Record<string, boolean> = {};
        Object.entries(prev).forEach(([key, value]) => {
          newModes[idMap[key] || key] = value;
        });
        newModes[savedId] = false;
        return newModes;
      });

      showNotification({
        title: t('canvas.save_success'),
        message: t('canvas.save_success_message'),
        color: 'green',
      });
    } catch (error) {
      showNotification({
        title: t('canvas.save_error'),
        message: t('canvas.save_error_message'),
        color: 'red',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteDetection = async (detectionId: string) => {
    // 删除店铺数据
    const updatedDetections = editingDetections.filter((detection) => detection.id !== detectionId);
    setEditingDetections(updatedDetections);

    if (selectedDetectionId === detectionId) {
      setSelectedDetectionId(null);
    }
    setLocalEditModes((prev) => {
      const newModes = { ...prev };
      delete newModes[detectionId];
      return newModes;
    });

    // 调用全局保存API
    if (!previewImage || !aiId) return;

    setIsSaving(true);
    try {
      const storeData = JSON.stringify(updatedDetections);
      await saveStoreInfo({
        image_id: previewImage.id,
        ai_id: aiId,
        store_data: storeData,
      });

      // 更新本地状态
      setOriginalDetections([...updatedDetections]);

      // 更新images中的数据
      setImages((prev) =>
        prev.map((img) => (img.id === previewImage.id ? { ...img, detections: [...updatedDetections] } : img))
      );

      // 更新previewImage
      setPreviewImage((prev) => (prev ? { ...prev, detections: [...updatedDetections] } : null));

      // 退出编辑模式
      setEditingDetectionId(null);

      showNotification({
        title: t('canvas.delete_success'),
        message: t('canvas.delete_shop_success_message'),
        color: 'green',
      });
    } catch (error) {
      showNotification({
        title: t('canvas.delete_error'),
        message: t('canvas.delete_shop_error_message'),
        color: 'red',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddDetection = () => {
    const newDetection: StoreDetection = {
      id: `new_${Date.now()}`,
      name: '新店铺',
      x: 0,
      y: 0,
    };

    setEditingDetections((prev) => [...prev, newDetection]);
    setEditingDetectionId(newDetection.id);
    setSelectedDetectionId(newDetection.id);
  };

  const handleImageClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!editingDetectionId || !previewImage || !imageContainerRef.current || !imageRef.current) return;

    const containerRect = imageContainerRef.current.getBoundingClientRect();
    const imageElement = imageRef.current;

    // 获取原始图片尺寸
    const naturalWidth = previewImage.width || imageElement.naturalWidth;
    const naturalHeight = previewImage.height || imageElement.naturalHeight;

    // 获取容器div的尺寸（由于display: inline-block，容器会自动适应img尺寸）
    const containerWidth = imageContainerRef.current.clientWidth;
    const containerHeight = imageContainerRef.current.clientHeight;

    // 计算点击位置相对于容器div的百分比位置
    const clickXPercent = (event.clientX - containerRect.left) / containerWidth;
    const clickYPercent = (event.clientY - containerRect.top) / containerHeight;

    // 转换为原始图片坐标
    let x = Math.round(clickXPercent * naturalWidth);
    let y = Math.round(clickYPercent * naturalHeight);

    // 确保坐标在图片范围内
    x = Math.max(0, Math.min(x, naturalWidth));
    y = Math.max(0, Math.min(y, naturalHeight));

    // 如果正在编辑某个检测点，更新其坐标（但不退出编辑模式）
    if (editingDetectionId) {
      // 直接更新坐标，不调用 handleSaveDetection（因为那会退出编辑模式）
      setEditingDetections((prev) =>
        prev.map((detection) => (detection.id === editingDetectionId ? { ...detection, x, y } : detection))
      );
    }
  };

  const handleCancelEdit = (detectionId: string) => {
    // 检查是否是新店铺（ID以"new_"开头）
    if (detectionId.startsWith('new_')) {
      // 新店铺：完全移除
      setEditingDetections((prev) => prev.filter((d) => d.id !== detectionId));
      if (selectedDetectionId === detectionId) {
        setSelectedDetectionId(null);
      }
    } else {
      // 现有店铺：恢复原始数据
      const originalDetection = originalDetections.find((d) => d.id === detectionId);
      if (originalDetection) {
        setEditingDetections((prev) => prev.map((d) => (d.id === detectionId ? { ...originalDetection } : d)));
      }
    }
    setEditingDetectionId(null);
    setLocalEditModes((prev) => ({ ...prev, [detectionId]: false }));
  };

  // 修改关闭预览的处理函数
  const handleClosePreview = () => {
    setPreviewImage(null);
    setSelectedDetectionId(null);
    setEditingDetectionId(null);
    setEditingDetections([]);
    setOriginalDetections([]);
    setLocalEditModes({});
  };

  return (
    <Stack gap="lg">
      {/* General Canvas卡片 */}
      <Card
        shadow="sm"
        padding="lg"
        radius="md"
        withBorder
        style={{
          borderColor: canvasType === 'general' ? '#3b82f6' : undefined,
          borderWidth: canvasType === 'general' ? '2px' : undefined,
        }}
      >
        <Stack gap="md">
          <Group justify="space-between" align="center">
            <Group gap="md">
              <Text size="lg" fw={600}>
                General Canvas
              </Text>
              <Group gap="xs">
                {getCanvasStatus('general').isEnabled && (
                  <Badge color="green" variant="light">
                    {t('canvas.enabled')}
                  </Badge>
                )}
                {getCanvasStatus('general').isConfigured && (
                  <Badge color="blue" variant="light">
                    {t('canvas.configured')}
                  </Badge>
                )}
              </Group>
            </Group>
            <Group gap="md">
              <Switch
                checked={canvasType === 'general'}
                onChange={(e) => handleGeneralSwitch(e.currentTarget.checked)}
                size="md"
              />
              <ActionIcon
                onClick={() => setGeneralExpanded(!generalExpanded)}
                variant="subtle"
                disabled={canvasType !== 'general'}
              >
                {generalExpanded ? <IconChevronUp size={20} /> : <IconChevronDown size={20} />}
              </ActionIcon>
            </Group>
          </Group>

          <Collapse in={canvasType === 'general' && generalExpanded}>
            <Stack gap="md" mt="md">
              <TextInput
                label={
                  <Group align="center" gap={4}>
                    <Text>Canvas Webhook URL</Text>
                    <ActionIcon onClick={open} variant="transparent" size="xs">
                      <IconHelp size={16} />
                    </ActionIcon>
                  </Group>
                }
                placeholder="https://example.com/webhook"
                value={webhookUrl}
                error={webhookUrlError}
                onChange={(e) => {
                  setWebhookUrl(e.currentTarget.value);
                  if (webhookUrlError) setWebhookUrlError('');
                }}
                rightSection={
                  webhookUrl ? (
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      onClick={() => {
                        setWebhookUrl('');
                        onOverrideChangeForConfigForm('preview_panel_api')('');
                      }}
                    >
                      <IconX size={16} />
                    </ActionIcon>
                  ) : null
                }
                onBlur={(e) => {
                  const newUrl = e.currentTarget.value.trim();
                  // URL 格式校验：对齐后端 is_valid_url（接受 a.com / http://b.com / https://x.com/y/z），
                  // 非法时不同步到 form、不触发保存，避免配置静默丢失
                  const isValidFormat =
                    !newUrl || /^(https?:\/\/)?[\w-]+(\.[\w-]+)+(:\d+)?(\/[^\s]*)?(\?[^\s]*)?$/.test(newUrl);
                  if (!isValidFormat) {
                    setWebhookUrlError(t('canvas.webhook_url_invalid'));
                    return;
                  }
                  setWebhookUrlError('');
                  setWebhookUrl(newUrl);
                  // 同步到form并触发保存
                  documentForm.setFieldValue('preview_panel_api', newUrl);
                  onOverrideChangeForConfigForm('preview_panel_api')(e);
                }}
              />
            </Stack>
          </Collapse>

          {canvasType !== 'general' && (
            <Group gap="xs" opacity={0.6}>
              <IconLock size={16} />
              <Text size="sm" color="dimmed">
                {t('canvas.enable_to_configure')}
              </Text>
            </Group>
          )}
        </Stack>
      </Card>

      {/* Map Canvas卡片 */}
      <Card
        shadow="sm"
        padding="lg"
        radius="md"
        withBorder
        style={{
          borderColor: canvasType === 'map' ? '#3b82f6' : undefined,
          borderWidth: canvasType === 'map' ? '2px' : undefined,
        }}
      >
        <Stack gap="md">
          <Group justify="space-between" align="center">
            <Group gap="md">
              <Text size="lg" fw={600}>
                Map Canvas
              </Text>
              <Group gap="xs">
                {getCanvasStatus('map').isEnabled && (
                  <Badge color="green" variant="light">
                    {t('canvas.enabled')}
                  </Badge>
                )}
                {getCanvasStatus('map').isConfigured && (
                  <Badge color="blue" variant="light">
                    {t('canvas.configured')}
                  </Badge>
                )}
              </Group>
            </Group>
            <Group gap="md">
              <Switch
                checked={canvasType === 'map'}
                onChange={(e) => handleMapSwitch(e.currentTarget.checked)}
                size="md"
              />
              <ActionIcon onClick={() => setMapExpanded(!mapExpanded)} variant="subtle" disabled={canvasType !== 'map'}>
                {mapExpanded ? <IconChevronUp size={20} /> : <IconChevronDown size={20} />}
              </ActionIcon>
            </Group>
          </Group>
          <Collapse in={canvasType === 'map' && mapExpanded}>
            <Stack gap="md" mt="md">
              {/* 拖拽上传区域 */}
              <Box
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-all ${
                  isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <IconUpload size={48} className="mx-auto mb-4 text-gray-400" />
                <Text size="lg" fw={500} mb="xs">
                  {t('canvas.drag_drop_images')}
                </Text>
                <Text size="sm" color="dimmed" mb="md">
                  {t('canvas.or_click_to_select')}
                </Text>
                <FileInput
                  accept="image/*"
                  multiple
                  value={fileInputValue}
                  onChange={(files) => {
                    setFileInputValue(files || []);
                    if (files && files.length > 0) {
                      handleFileUpload(files);
                    }
                  }}
                  style={{ display: 'none' }}
                  id="file-upload"
                />
                <ActionIcon variant="outline" onClick={() => document.getElementById('file-upload')?.click()} size="lg">
                  <IconUpload size={18} />
                </ActionIcon>
              </Box>

              {/* 加载状态 */}
              {loadingImages && (
                <Group gap="xs" justify="center" className="py-4">
                  <Loader size="sm" />
                  <Text size="sm" color="dimmed">
                    {t('canvas.loading_images')}
                  </Text>
                </Group>
              )}

              {/* 图片列表 */}
              {!loadingImages && images.length > 0 && (
                <SimpleGrid cols={3} gap="md">
                  {images.map((image) => (
                    <Card key={image.id} shadow="sm" padding="sm" radius="md" withBorder>
                      <Card.Section>
                        <Image
                          src={image.url || image.path}
                          height={160}
                          alt={image.original_filename}
                          fit="cover"
                          onError={(e) => {
                            // console.error('图片加载失败:', {
                            //   src: image.url || image.path,
                            //   image: image,
                            //   error: e
                            // });
                          }}
                          onLoad={() => {
                            // console.log('图片加载成功:', image.url || image.path);
                          }}
                        />
                      </Card.Section>

                      <Stack gap="xs" mt="sm">
                        {/* 图片名称编辑区域 */}
                        {editingImageId === image.id ? (
                          <Group gap="xs" align="center">
                            <TextInput
                              value={editingImageName}
                              onChange={(e) => setEditingImageName(e.currentTarget.value)}
                              placeholder="图片名称"
                              size="xs"
                              style={{ flex: 1 }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleSaveImageName();
                                } else if (e.key === 'Escape') {
                                  handleCancelEditImageName();
                                }
                              }}
                              autoFocus
                            />
                            <ActionIcon size="xs" variant="filled" color="gray" onClick={handleCancelEditImageName}>
                              <IconX size={12} />
                            </ActionIcon>
                            <ActionIcon
                              size="xs"
                              variant="filled"
                              color="green"
                              onClick={handleSaveImageName}
                              loading={isSaving}
                            >
                              <IconCheck size={12} />
                            </ActionIcon>
                          </Group>
                        ) : (
                          <Group gap="xs" align="center">
                            <Text size="sm" fw={500} lineClamp={1} style={{ flex: 1 }}>
                              {image.original_filename}
                            </Text>
                            <ActionIcon
                              size="xs"
                              variant="subtle"
                              color="gray"
                              onClick={() => handleEditImageName(image.id, image.original_filename)}
                            >
                              <IconEdit size={12} />
                            </ActionIcon>
                          </Group>
                        )}

                        <Group justify="space-between">
                          <ActionIcon color="blue" variant="subtle" onClick={() => handleImagePreview(image)}>
                            <IconEye size={18} />
                          </ActionIcon>
                          <ActionIcon color="red" variant="subtle" onClick={() => handleDeleteImage(image.id)}>
                            <IconTrash size={18} />
                          </ActionIcon>
                        </Group>
                      </Stack>
                    </Card>
                  ))}
                </SimpleGrid>
              )}
            </Stack>
          </Collapse>

          {canvasType !== 'map' && (
            <Group gap="xs" opacity={0.6}>
              <IconLock size={16} />
              <Text size="sm" color="dimmed">
                {t('canvas.enable_to_configure')}
              </Text>
            </Group>
          )}
        </Stack>
      </Card>

      {/* API文档抽屉 */}
      <Drawer
        opened={opened}
        onClose={close}
        title={
          <Stack gap={2}>
            <Text fw={500}>Canvas Webhook API</Text>
            <Text c="gray.6" size="sm">
              {t('canvas.api_desc')}
            </Text>
          </Stack>
        }
        justify="flex-end"
        size="xl"
      >
        <Text fw={500}>Request method</Text>
        <Code>POST</Code>
        <Space h="lg" />
        <Text fw={500}>Request body</Text>
        <Divider my="xs" size={1} />
        <Group>
          <Text fw={500} size="sm">
            id
          </Text>
          <Text c="grey" size="sm">
            string
          </Text>
          <Text c="red" size="sm">
            Required
          </Text>
        </Group>
        <Text size="sm">{t('canvas.id_desc')}</Text>
        <Divider my="xs" size={1} />
        <Group>
          <Text fw={500} size="sm">
            question
          </Text>
          <Text c="grey" size="sm">
            string
          </Text>
          <Text c="red" size="sm">
            Required
          </Text>
        </Group>
        <Text size="sm">{t('canvas.question_desc')}</Text>
        <Divider my="xs" size={1} />
        <Group>
          <Text fw={500} size="sm">
            answer
          </Text>
          <Text c="grey" size="sm">
            string
          </Text>
          <Text c="red" size="sm">
            Required
          </Text>
        </Group>
        <Text size="sm">{t('canvas.answer_desc')}</Text>
        <Divider my="xs" size={1} />
        <Group>
          <Text fw={500} size="sm">
            name
          </Text>
          <Text c="grey" size="sm">
            string
          </Text>
          <Text c="red" size="sm">
            Required
          </Text>
        </Group>
        <Text size="sm">{t('canvas.bot_name_desc')}</Text>
        <Divider my="xs" size={1} />
        <Group>
          <Text fw={500} size="sm">
            discrible
          </Text>
          <Text c="grey" size="sm">
            string
          </Text>
          <Text c="red" size="sm">
            Required
          </Text>
        </Group>
        <Text size="sm">{t('canvas.discrible_desc')}</Text>
        <Space h="lg" />
        <CodeHighlight code={requestBody} language="json" />
        <Space h="lg" />
        <Text fw={500}>Response body</Text>
        <Divider my="xs" size={1} />
        <Group>
          <Text fw={500} size="sm">
            show_popup
          </Text>
          <Text c="grey" size="sm">
            boolean
          </Text>
          <Text c="red" size="sm">
            Required
          </Text>
        </Group>
        <Text size="sm">{t('canvas.show_popup_desc')}</Text>
        <Divider my="xs" size={1} />
        <Group>
          <Text fw={500} size="sm">
            canvas_url
          </Text>
          <Text c="grey" size="sm">
            string
          </Text>
          <Text c="red" size="sm">
            Required
          </Text>
        </Group>
        <Text size="sm">{t('canvas.canvas_url_desc')}</Text>
        <Divider my="xs" size={1} />
        <Group>
          <Text fw={500} size="sm">
            title
          </Text>
          <Text c="grey" size="sm">
            string
          </Text>
          <Text c="red" size="sm">
            Required
          </Text>
        </Group>
        <Text size="sm">{t('canvas.title_desc')}</Text>
        <Space h="lg" />
        <CodeHighlight code={responseBody} language="json" />
      </Drawer>

      {/* 图片预览模态框 */}
      <Modal
        opened={!!previewImage}
        onClose={handleClosePreview}
        size="95%"
        title={
          <Group justify="space-between" style={{ width: '100%' }}>
            <Text>{previewImage?.original_filename}</Text>
            <Group gap="xs">{/* 移除全局保存按钮，改为每个店铺独立保存 */}</Group>
          </Group>
        }
        styles={{
          body: { padding: 0, overflow: 'auto' },
          header: { padding: '1rem' },
          content: { maxHeight: '95vh' },
        }}
      >
        {previewImage && (
          <Stack gap="lg" style={{ padding: '1rem' }}>
            <Box
              style={{
                position: 'relative',
                textAlign: 'center',
                overflow: 'visible',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '200px',
              }}
            >
              {/* 图片容器div，包含img和坐标点位 */}
              <div
                ref={imageContainerRef}
                style={{
                  position: 'relative',
                  display: 'inline-block',
                }}
                onClick={handleImageClick}
              >
                <Image
                  ref={imageRef}
                  src={previewImage.url || previewImage.path}
                  alt={previewImage.original_filename}
                  radius="md"
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '75vh',
                    objectFit: 'cover',
                    cursor: editingDetectionId ? 'crosshair' : 'default',
                    display: 'block',
                  }}
                  onError={(e) => {
                    console.error('预览图片加载失败:', {
                      src: previewImage.url || previewImage.path,
                      previewImage: previewImage,
                      error: e,
                    });
                  }}
                  onLoad={() => {
                    // 图片加载完成
                  }}
                />

                {/* 标注店铺位置 */}
                {editingDetections.map((detection) => {
                  // 计算坐标点在容器div中的位置
                  let leftPercent = 0;
                  let topPercent = 0;

                  if (previewImage) {
                    const naturalWidth = previewImage.width || imageRef.current?.naturalWidth || 1;
                    const naturalHeight = previewImage.height || imageRef.current?.naturalHeight || 1;

                    // 基于原始图片尺寸计算百分比位置
                    leftPercent = (detection.x / naturalWidth) * 100;
                    topPercent = (detection.y / naturalHeight) * 100;
                  }

                  const isEditingThis = localEditModes[detection.id];
                  const isSelected = selectedDetectionId === detection.id;

                  return (
                    <Box
                      key={detection.id}
                      style={{
                        position: 'absolute',
                        left: `${leftPercent}%`,
                        top: `${topPercent}%`,
                        transform: 'translate(-50%, -50%)',
                      }}
                    >
                      {/* 主要的店铺标注点 */}
                      <Box
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          backgroundColor: isEditingThis ? '#4caf50' : isSelected ? '#1976d2' : '#f44336',
                          border: '2px solid white',
                          cursor: 'pointer',
                          transition: 'background-color 0.3s ease',
                          zIndex: 1,
                          boxShadow: isEditingThis ? '0 0 10px rgba(76, 175, 80, 0.5)' : 'none',
                        }}
                        title={detection.name}
                        onClick={(e) => {
                          e.stopPropagation();
                          // 如果当前有店铺在编辑状态，只允许选中正在编辑的店铺
                          if (editingDetectionId && editingDetectionId !== detection.id) {
                            return; // 不允许选中其他店铺
                          }
                          setSelectedDetectionId(detection.id);
                          // 滚动到对应的店铺卡片并添加动画
                          scrollToDetectionCard(detection.id);
                        }}
                      />
                    </Box>
                  );
                })}
              </div>
            </Box>

            {/* 检测结果区域 */}
            <Box>
              <Group justify="space-between" align="center" mb="md">
                <Text fw={500} size="lg">
                  {t('canvas.detection_results')}
                </Text>
                <Group gap="xs">
                  <Text size="sm" c="dimmed">
                    {t('canvas.detection_count', {
                      count: editingDetections.length,
                    })}
                  </Text>
                  <ActionIcon variant="filled" color="blue" size="sm" onClick={handleAddDetection}>
                    <IconPlus size={16} />
                  </ActionIcon>
                </Group>
              </Group>

              {/* 检测结果网格布局 */}
              {editingDetections.length > 0 ? (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                    gap: '1rem',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    paddingRight: '8px',
                    paddingTop: '12px', // 为删除按钮预留顶部空间
                  }}
                >
                  {editingDetections.map((detection) => (
                    <EditableDetectionCard
                      key={detection.id}
                      detection={detection}
                      isSelected={selectedDetectionId === detection.id}
                      isEditing={editingDetectionId === detection.id}
                      onSelect={() => {
                        // 如果当前有其他店铺在编辑状态，不允许选中这个店铺
                        if (editingDetectionId && editingDetectionId !== detection.id) {
                          return; // 不允许选中其他店铺
                        }
                        setSelectedDetectionId(detection.id);
                      }}
                      onEdit={() => handleEditDetection(detection.id)}
                      onUpdate={(id, name, x, y) => {
                        // 实时更新数据但不退出编辑模式
                        setEditingDetections((prev) =>
                          prev.map((detection) => (detection.id === id ? { ...detection, name, x, y } : detection))
                        );
                      }}
                      onSave={handleSaveDetection}
                      onCancel={() => handleCancelEdit(detection.id)}
                      onDelete={handleDeleteDetection}
                      cardRef={(element) => {
                        detectionCardRefs.current[detection.id] = element;
                      }}
                      t={t}
                      imageWidth={previewImage.width || 800}
                      imageHeight={previewImage.height || 600}
                    />
                  ))}
                </div>
              ) : (
                <Text c="dimmed" ta="center" py="xl">
                  {t('canvas.no_detection_results')}
                </Text>
              )}
            </Box>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
});

// 可编辑的检测结果卡片组件
interface EditableDetectionCardProps {
  detection: StoreDetection;
  isSelected: boolean;
  isEditing: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onUpdate: (id: string, name: string, x: number, y: number) => void;
  onSave: (id: string, name: string, x: number, y: number) => void;
  onCancel: () => void;
  onDelete: (id: string) => void;
  t: (key: string, options?: any) => string;
  imageWidth?: number;
  imageHeight?: number;
  cardRef?: (element: HTMLDivElement | null) => void;
}

const EditableDetectionCard = ({
  detection,
  isSelected,
  isEditing,
  onSelect,
  onEdit,
  onUpdate,
  onSave,
  onCancel,
  onDelete,
  t,
  imageWidth = 800,
  imageHeight = 600,
  cardRef,
}: EditableDetectionCardProps) => {
  const [editName, setEditName] = useState(detection.name);
  const [editX, setEditX] = useState(detection.x.toString());
  const [editY, setEditY] = useState(detection.y.toString());

  // 当检测数据更新时，同步更新表单值
  useEffect(() => {
    setEditName(detection.name);
    setEditX(detection.x.toString());
    setEditY(detection.y.toString());
  }, [detection.name, detection.x, detection.y]);

  const handleSave = () => {
    let x = parseInt(editX) || 0;
    let y = parseInt(editY) || 0;

    // 限制坐标范围
    x = Math.max(0, Math.min(x, imageWidth));
    y = Math.max(0, Math.min(y, imageHeight));

    onSave(detection.id, editName, x, y);
  };

  const handleXChange = (value: string) => {
    const numValue = parseInt(value);
    if (isNaN(numValue)) {
      setEditX('');
    } else {
      const clampedValue = Math.max(0, Math.min(numValue, imageWidth));
      setEditX(clampedValue.toString());
      // 实时更新坐标到父组件，但不退出编辑模式
      onUpdate(detection.id, editName, clampedValue, parseInt(editY) || detection.y);
    }
  };

  const handleYChange = (value: string) => {
    const numValue = parseInt(value);
    if (isNaN(numValue)) {
      setEditY('');
    } else {
      const clampedValue = Math.max(0, Math.min(numValue, imageHeight));
      setEditY(clampedValue.toString());
      // 实时更新坐标到父组件，但不退出编辑模式
      onUpdate(detection.id, editName, parseInt(editX) || detection.x, clampedValue);
    }
  };

  const handleCancel = () => {
    setEditName(detection.name);
    setEditX(detection.x.toString());
    setEditY(detection.y.toString());
    onCancel();
  };

  if (isEditing) {
    return (
      <Paper
        p="sm"
        withBorder
        shadow="xs"
        ref={cardRef}
        style={{
          borderColor: '#1976d2',
          backgroundColor: '#e3f2fd',
          position: 'relative',
        }}
      >
        {/* 右上角删除按钮 - 只对现有店铺显示，新店铺不显示 */}
        {!detection.id.startsWith('new_') && (
          <ActionIcon
            size="xs"
            color="red"
            variant="filled"
            style={{
              position: 'absolute',
              top: -8,
              right: -8,
              width: 18,
              height: 18,
              zIndex: 20,
            }}
            onClick={(e) => {
              e.stopPropagation();
              onDelete(detection.id);
            }}
          >
            <IconX size={10} />
          </ActionIcon>
        )}

        <Stack gap="xs">
          <TextInput
            value={editName}
            onChange={(e) => {
              setEditName(e.currentTarget.value);
              // 实时更新名称到父组件
              onUpdate(
                detection.id,
                e.currentTarget.value,
                parseInt(editX) || detection.x,
                parseInt(editY) || detection.y
              );
            }}
            placeholder="店铺名称"
            size="xs"
          />
          <Group gap="xs">
            <TextInput
              value={editX}
              onChange={(e) => handleXChange(e.currentTarget.value)}
              placeholder={`X (0-${imageWidth})`}
              size="xs"
              style={{ flex: 1 }}
              type="number"
              min={0}
              max={imageWidth}
            />
            <TextInput
              value={editY}
              onChange={(e) => handleYChange(e.currentTarget.value)}
              placeholder={`Y (0-${imageHeight})`}
              size="xs"
              style={{ flex: 1 }}
              type="number"
              min={0}
              max={imageHeight}
            />
          </Group>
          <Group gap="xs" justify="flex-end">
            <ActionIcon size="sm" variant="outline" color="gray" onClick={handleCancel}>
              <IconX size={14} />
            </ActionIcon>
            <ActionIcon size="sm" variant="filled" color="green" onClick={handleSave}>
              <IconCheck size={14} />
            </ActionIcon>
          </Group>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper
      p="sm"
      withBorder
      shadow="xs"
      ref={cardRef}
      style={{
        backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
        borderColor: isSelected ? '#1976d2' : undefined,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        position: 'relative',
      }}
      onClick={onSelect}
    >
      {/* 右下角编辑按钮 */}
      {!isEditing && isSelected && (
        <ActionIcon
          size="xs"
          color="blue"
          variant="filled"
          style={{
            position: 'absolute',
            bottom: 4,
            right: 4,
            width: 16,
            height: 16,
            zIndex: 10,
          }}
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
        >
          <IconEdit size={10} />
        </ActionIcon>
      )}

      <Stack gap="xs" style={{ flex: 1 }}>
        <Text fw={500} size="sm" c={isSelected ? '#1976d2' : undefined}>
          {detection.name}
        </Text>
        <Group justify="space-between" align="flex-end">
          <Text size="xs" c="dimmed">
            {t('canvas.position')}: ({detection.x}, {detection.y})
          </Text>
        </Group>
      </Stack>
    </Paper>
  );
};

CanvasForm.displayName = 'CanvasForm';
